import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { ArrowRight, ChevronDown, Loader2 } from 'lucide-react'

import { cn } from '@/lib/cn'
import { useIsDesktop, usePrefersReducedMotion } from '@/lib/use-media-query'
import WeekendActivityNotice from './weekend-activity-notice'

import {
  type AdminWorkSession,
} from './use-admin-sessions'
import {
  type CalendarWeekDay,
  type CalendarWeekVacation,
  type CalendarWeekWorking,
  getFocusDayIndex,
  useCalendarWeek,
  useTodayIso,
} from './use-calendar-week'

interface Props {
  weekStart: string
  /**
   * Licznik podbijany przy każdym kliknięciu „Dziś".
   * Sam `weekStart` nie wystarczy: gdy admin jest już w bieżącym tygodniu,
   * URL się nie zmienia, więc nie ma czego obserwować — a widok i tak ma
   * wrócić na dzisiejszy dzień.
   */
  focusToken: number
  onUserClick: (date: string, userId: string) => void
}

const MONTHS_PL = [
  'stycznia',
  'lutego',
  'marca',
  'kwietnia',
  'maja',
  'czerwca',
  'lipca',
  'sierpnia',
  'września',
  'października',
  'listopada',
  'grudnia',
] as const

function monthLabel(date: string): string {
  return MONTHS_PL[parseInt(date.slice(5, 7), 10) - 1] ?? ''
}

function formatTotalHours(ms: number): string {
  if (ms <= 0) return ''
  const totalMin = Math.floor(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${String(m).padStart(2, '0')}`
}

function formatStartTime(session: AdminWorkSession): string {
  return format(parseISO(session.startedAt), 'HH:mm')
}

function formatVacationRange(startDate: string, endDate: string): string {
  const fmt = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`
  if (startDate === endDate) return fmt(startDate)
  return `${fmt(startDate)} — ${fmt(endDate)}`
}

type BadgeVariant = 'accent' | 'warning' | 'success' | 'danger'

/**
 * Znaczenie niesie TŁO i obramowanie, etykieta zostaje w `text-text-primary`.
 *
 * Poprzedni wzorzec (`text-warning` na `bg-warning/20`) miał zmierzony kontrast
 * 2.58:1 w jasnym i 3.90:1 w ciemnym motywie — przy 10 px to daleko poniżej
 * WCAG AA. Siedem z ośmiu par kolor-na-swoim-tincie oblewało próg, a żaden test
 * palety tego nie widział, bo para powstawała dopiero w komponencie
 * (patrz CLAUDE.md — „para wypełnienie + tusz jest nierozłączna").
 * Tusz na tincie mierzy teraz 10.6:1 – 15.2:1 w obu motywach.
 */
const BADGE_VARIANT_CLS: Record<BadgeVariant, string> = {
  accent: 'bg-brand-accent/20 border-brand-accent/50',
  warning: 'bg-warning/20 border-warning/50',
  success: 'bg-success/20 border-success/50',
  danger: 'bg-danger/20 border-danger/50',
}

const VACATION_BADGE: Record<
  string,
  { label: string; variant: BadgeVariant }
> = {
  WYPOCZYNKOWY: { label: 'urlop', variant: 'success' },
  NA_ZADANIE: { label: 'na żąd.', variant: 'success' },
  BEZPLATNY: { label: 'bezpł.', variant: 'success' },
  OPIEKA_NAD_DZIECKIEM: { label: 'opieka', variant: 'success' },
  CHOROBOWY: { label: 'L4', variant: 'danger' },
}

function StatusBadge({
  label,
  variant,
}: {
  label: string
  variant: BadgeVariant
}) {
  return (
    <span
      className={cn(
        'flex-shrink-0 rounded whitespace-nowrap border font-semibold uppercase',
        'px-1.5 py-0.5 text-[10px] tracking-wider text-text-primary',
        BADGE_VARIANT_CLS[variant],
      )}
    >
      {label}
    </span>
  )
}

/** Wspólny wygląd klikalnej karty pracownika — min. 44 px wysokości (Apple HIG). */
const PERSON_CARD_CLS = cn(
  'w-full text-left rounded-lg border transition-colors duration-200 ease-brand',
  'px-2.5 py-2 min-h-[44px]',
  'bg-bg-surface border-border-subtle',
  'hover:border-brand-accent/50 hover:bg-bg-canvas',
)

function WorkingUserCard({
  entry,
  onVacationToo,
  onClick,
}: {
  entry: CalendarWeekWorking
  onVacationToo: boolean
  onClick: () => void
}) {
  let badge: { label: string; variant: BadgeVariant } | null = null
  if (entry.hasOpenSession) badge = { label: 'otwarta', variant: 'accent' }
  else if (entry.hasOutsideClinic) badge = { label: 'poza', variant: 'warning' }

  const openSession = entry.sessions.find((s) => !s.endedAt)
  const subtitle = openSession
    ? `rozpoczęta o ${formatStartTime(openSession)}`
    : formatTotalHours(entry.totalDurationMs)

  return (
    <button type="button" onClick={onClick} className={PERSON_CARD_CLS}>
      <div className="flex items-start justify-between gap-1.5 mb-0.5">
        <p className="text-sm font-medium text-text-primary truncate flex-1 min-w-0 leading-tight">
          {entry.user.name}
        </p>
        {badge && <StatusBadge label={badge.label} variant={badge.variant} />}
      </div>
      {entry.user.position && (
        <p className="text-xs text-text-muted truncate leading-tight">
          {entry.user.position.name}
        </p>
      )}
      <p className="text-xs text-text-muted font-mono tabular-nums mt-1 leading-tight">
        {subtitle}
      </p>
      {onVacationToo && (
        <p className="text-[11px] text-text-muted mt-0.5">także urlop</p>
      )}
    </button>
  )
}

function VacationUserCard({
  entry,
  onClick,
}: {
  entry: CalendarWeekVacation
  onClick: () => void
}) {
  const badge = VACATION_BADGE[entry.request.type] ?? {
    label: entry.request.type.toLowerCase(),
    variant: 'success' as BadgeVariant,
  }
  const range = formatVacationRange(
    entry.request.startDate,
    entry.request.endDate,
  )

  return (
    <button type="button" onClick={onClick} className={PERSON_CARD_CLS}>
      <div className="flex items-start justify-between gap-1.5 mb-0.5">
        <p className="text-sm font-medium text-text-primary truncate flex-1 min-w-0 leading-tight">
          {entry.user.name}
        </p>
        <StatusBadge label={badge.label} variant={badge.variant} />
      </div>
      {entry.user.position && (
        <p className="text-xs text-text-muted truncate leading-tight">
          {entry.user.position.name}
        </p>
      )}
      <p className="text-xs text-text-muted font-mono tabular-nums mt-1 leading-tight">
        {range}
      </p>
    </button>
  )
}

function SectionLabel({ label, count }: { label: string; count: number }) {
  return (
    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-muted mb-1.5">
      {label}
      <span className="ml-2 tabular-nums font-medium">{count}</span>
    </p>
  )
}

/**
 * Lista osób bez aktywności — zwijana, bo na telefonie potrafi być dłuższa
 * niż wszystko, co w danym dniu faktycznie się wydarzyło, i spychała sekcje
 * „W pracy" oraz „Na urlopie" poza ekran.
 */
function InactiveSection({
  users,
  defaultOpen,
  onUserClick,
}: {
  users: CalendarWeekDay['inactiveUsers']
  defaultOpen: boolean
  onUserClick: (userId: string) => void
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'w-full flex items-center gap-1.5 min-h-[32px] rounded-md',
          'text-[11px] font-bold uppercase tracking-[0.14em] text-text-muted',
          'hover:text-text-primary transition-colors duration-150 ease-brand',
        )}
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 transition-transform duration-200 ease-brand',
            !open && '-rotate-90',
          )}
          aria-hidden
        />
        Brak aktywności
        <span className="tabular-nums font-medium">{users.length}</span>
      </button>

      {open && (
        <div className="space-y-0.5 mt-1">
          {users.map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => onUserClick(user.id)}
              className={cn(
                'w-full text-left rounded-md transition-colors duration-150 ease-brand',
                'px-2 py-1.5 min-h-[32px]',
                'text-xs text-text-muted',
                'hover:text-text-primary hover:bg-bg-surface',
              )}
            >
              {user.name}
            </button>
          ))}
        </div>
      )}
    </section>
  )
}

function CalendarWeekCell({
  day,
  totalActiveUsers,
  inactiveOpenByDefault,
  onUserClick,
}: {
  day: CalendarWeekDay
  totalActiveUsers: number
  inactiveOpenByDefault: boolean
  onUserClick: (date: string, userId: string) => void
}) {
  const workingCount = day.workingSessions.length
  const vacationsCount = day.vacations.length
  const inactiveCount = day.inactiveUsers.length

  const isEmpty = workingCount === 0 && vacationsCount === 0 && inactiveCount === 0
  const vacationUserIds = new Set(day.vacations.map((v) => v.user.id))

  return (
    <article
      aria-current={day.isToday ? 'date' : undefined}
      className={cn(
        'flex flex-col rounded-2xl border bg-bg-elevated overflow-hidden h-full',
        // Wysokość ograniczona, przewija się WNĘTRZE karty. Bez tego jeden
        // ruchliwy dzień rozciągał wszystkie pięć kolumn na desktopie,
        // a na telefonie karta z 15 osobami rosła na trzy ekrany.
        'min-h-[320px] max-h-[72svh] lg:min-h-[380px] lg:max-h-[calc(100svh-15rem)]',
        'transition-colors duration-300 ease-out',
        day.isToday && 'bg-brand-accent/5 border-brand-accent/60',
        !day.isToday && day.isPolishHoliday && 'bg-warning/5 border-warning/40',
        !day.isToday &&
          !day.isPolishHoliday &&
          'border-border-subtle hover:border-border-strong/60',
      )}
    >
      <header
        className={cn(
          'shrink-0 border-b border-border-subtle',
          'px-3 py-2.5 lg:px-3.5 lg:py-3',
        )}
      >
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="flex items-baseline gap-2 min-w-0">
            <span className="sr-only">
              {day.weekday}, {parseInt(day.date.slice(8, 10), 10)}{' '}
              {monthLabel(day.date)}
              {day.isToday ? ' — dzisiaj' : ''}
              {day.holidayName ? ` — ${day.holidayName}` : ''}
            </span>
            <span
              aria-hidden
              className={cn(
                'font-light leading-none tabular-nums',
                'text-2xl lg:text-[26px] 3xl:text-3xl',
                day.isPolishHoliday && !day.isToday
                  ? 'text-warning'
                  : 'text-text-primary',
              )}
            >
              {day.date.slice(8, 10)}
            </span>
            <span aria-hidden className="min-w-0">
              <span
                className={cn(
                  'block text-[10px] font-bold uppercase tracking-[0.16em] leading-tight',
                  day.isToday ? 'text-brand-accent' : 'text-text-muted',
                )}
              >
                {day.weekdayShort}
              </span>
              <span className="block text-[11px] text-text-muted leading-tight truncate">
                {monthLabel(day.date)}
              </span>
            </span>
          </h2>
          {day.isToday && (
            <span
              className="h-1.5 w-1.5 rounded-full bg-brand-accent flex-shrink-0"
              aria-hidden
            />
          )}
        </div>

        {day.holidayName && (
          <p
            aria-hidden
            className="text-[11px] text-text-primary mt-1.5 truncate font-medium"
            title={day.holidayName}
          >
            {day.holidayName}
          </p>
        )}
      </header>

      {/* Przewija się tylko treść — nagłówek z datą zostaje widoczny,
          więc przy długiej liście wiadomo, czyj to dzień. */}
      <div
        className={cn(
          'flex-1 overflow-y-auto overscroll-y-contain',
          'px-3 py-3 lg:px-3.5 lg:py-3',
          'space-y-3',
        )}
      >
        {workingCount > 0 && (
          <section>
            <SectionLabel label="W pracy" count={workingCount} />
            <div className="space-y-1.5">
              {day.workingSessions.map((entry) => (
                <WorkingUserCard
                  key={entry.user.id}
                  entry={entry}
                  onVacationToo={vacationUserIds.has(entry.user.id)}
                  onClick={() => onUserClick(day.date, entry.user.id)}
                />
              ))}
            </div>
          </section>
        )}

        {vacationsCount > 0 && (
          <section>
            <SectionLabel label="Na urlopie" count={vacationsCount} />
            <div className="space-y-1.5">
              {day.vacations.map((entry) => (
                <VacationUserCard
                  key={entry.user.id}
                  entry={entry}
                  onClick={() => onUserClick(day.date, entry.user.id)}
                />
              ))}
            </div>
          </section>
        )}

        {inactiveCount > 0 && (
          <InactiveSection
            users={day.inactiveUsers}
            defaultOpen={inactiveOpenByDefault}
            onUserClick={(userId) => onUserClick(day.date, userId)}
          />
        )}

        {isEmpty && <p className="text-xs text-text-muted">Brak danych</p>}
      </div>

      <div
        className={cn(
          'shrink-0 border-t border-border-subtle/60',
          'px-3 lg:px-3.5 py-1.5',
          'flex items-center justify-between gap-2',
        )}
      >
        <p className="text-[11px] text-text-muted">
          W pracy:{' '}
          <strong className="text-text-primary tabular-nums">
            {workingCount}
          </strong>
          <span className="mx-1">/</span>
          <span className="tabular-nums">{totalActiveUsers}</span>
        </p>
        <Link
          to={`/admin/day/${day.date}`}
          className={cn(
            'text-[11px] text-text-muted hover:text-brand-accent',
            'transition-colors duration-150 ease-brand',
            'inline-flex items-center gap-1 group rounded-md px-1 min-h-[36px]',
          )}
        >
          <span className="sr-only">Szczegóły dnia — {day.weekday}</span>
          <span aria-hidden>Szczegóły</span>
          <ArrowRight
            className="h-3.5 w-3.5 transition-transform duration-200 ease-brand group-hover:translate-x-0.5"
            aria-hidden
          />
        </Link>
      </div>
    </article>
  )
}

/**
 * Pasek dni — jednocześnie wskaźnik pozycji i nawigacja.
 *
 * Zastąpił kropki, które pokazywały „dzisiaj" niezależnie od tego, którą kartę
 * admin faktycznie ma na ekranie, i których nie dało się ani kliknąć,
 * ani osiągnąć klawiaturą.
 */
function DayStrip({
  days,
  selectedIndex,
  onSelect,
}: {
  days: CalendarWeekDay[]
  selectedIndex: number
  onSelect: (index: number) => void
}) {
  return (
    <nav
      aria-label="Wybór dnia tygodnia"
      className="grid grid-cols-5 gap-1.5 mb-3 lg:hidden"
    >
      {days.map((day, i) => {
        const selected = i === selectedIndex
        const count = day.workingSessions.length
        return (
          <button
            key={day.date}
            type="button"
            onClick={() => onSelect(i)}
            aria-current={selected ? 'true' : undefined}
            className={cn(
              'flex flex-col items-center justify-center gap-0.5 rounded-xl border',
              'min-h-[56px] px-1 py-1.5',
              'transition-colors duration-150 ease-brand',
              selected
                ? 'bg-brand-dark border-brand-dark text-bg-elevated'
                : cn(
                    'bg-bg-elevated text-text-muted',
                    day.isToday
                      ? 'border-brand-accent/60'
                      : 'border-border-subtle',
                  ),
            )}
          >
            <span className="sr-only">
              {day.weekday}, {parseInt(day.date.slice(8, 10), 10)}{' '}
              {monthLabel(day.date)}
              {day.isToday ? ' — dzisiaj' : ''} —{' '}
              {count === 0 ? 'nikt nie pracował' : `${count} w pracy`}
            </span>
            <span
              aria-hidden
              className={cn(
                'text-[10px] font-bold uppercase tracking-[0.1em] leading-none',
                !selected && day.isToday && 'text-brand-accent',
              )}
            >
              {day.weekdayShort}
            </span>
            <span
              aria-hidden
              className={cn(
                'text-base font-light leading-none tabular-nums',
                selected ? 'text-bg-elevated' : 'text-text-primary',
              )}
            >
              {day.date.slice(8, 10)}
            </span>
            <span
              aria-hidden
              className={cn(
                'text-[10px] leading-none tabular-nums',
                count === 0 && 'opacity-0',
              )}
            >
              {count}
            </span>
          </button>
        )
      })}
    </nav>
  )
}

export default function CalendarWeekView({
  weekStart,
  focusToken,
  onUserClick,
}: Props) {
  const {
    days,
    weekendActivity,
    totalActiveUsers,
    isLoading,
    isFetching,
    error,
  } = useCalendarWeek(weekStart)
  const today = useTodayIso()
  const reducedMotion = usePrefersReducedMotion()
  const isDesktop = useIsDesktop()

  const scrollerRef = useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(() =>
    getFocusDayIndex(weekStart, today),
  )

  // `today` czytane z refa, nie z deps: po północy ma się zmienić podświetlenie,
  // ale nie wyrywać adminowi karty, którą właśnie ogląda.
  const todayRef = useRef(today)
  useEffect(() => {
    todayRef.current = today
  }, [today])
  const prevWeekRef = useRef<string | null>(null)
  const positionedRef = useRef(false)

  function scrollToIndex(index: number, behavior: ScrollBehavior) {
    const el = scrollerRef.current
    if (!el) return
    // Na desktopie kontener jest gridem bez overflow — `scrollBy` byłby i tak
    // no-opem, ale jawny warunek trzyma intencję na wierzchu i nie zależy
    // od tego, na jakim breakpoincie siedzi `lg:overflow-visible`.
    if (el.scrollWidth <= el.clientWidth + 1) return
    const child = el.children[index] as HTMLElement | undefined
    if (!child) return
    // Delta z prostokątów, nie offsetLeft — działa niezależnie od tego,
    // który przodek jest offsetParentem.
    const delta =
      child.getBoundingClientRect().left - el.getBoundingClientRect().left
    if (Math.abs(delta) < 1) return
    el.scrollBy({ left: delta, behavior })
  }

  // Ustawienie dnia startowego: przy wejściu, przy zmianie tygodnia
  // i przy każdym kliknięciu „Dziś".
  useEffect(() => {
    const index = getFocusDayIndex(weekStart, todayRef.current)
    setSelectedIndex(index)

    // `isLoading` MUSI być w zależnościach: przy pierwszym wejściu komponent
    // renderuje spinner, więc karuzeli nie ma jeszcze w DOM i `scrollerRef`
    // jest puste. Bez ponownego przebiegu po dojściu danych karty montują się
    // na scrollLeft = 0 i widok znowu otwiera się na poniedziałku — czyli
    // dokładnie na błędzie, który ta zmiana naprawia.
    const el = scrollerRef.current
    if (!el) return

    const weekChanged = prevWeekRef.current !== weekStart
    const firstRun = !positionedRef.current
    prevWeekRef.current = weekStart
    positionedRef.current = true

    // Pierwsze ustawienie i skok na inny tydzień — bez animacji.
    // Powrót na „Dziś" w tym samym tygodniu — płynnie.
    const behavior: ScrollBehavior =
      firstRun || weekChanged || reducedMotion ? 'auto' : 'smooth'

    // rAF czeka na layout, żeby prostokąty kart były już właściwe.
    const raf = requestAnimationFrame(() => scrollToIndex(index, behavior))
    return () => cancelAnimationFrame(raf)
  }, [weekStart, focusToken, reducedMotion, isLoading])

  // Swipe → pasek dni. Bez tego wskaźnik pokazywałby co innego niż ekran.
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    let raf = 0
    function onScroll() {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const node = scrollerRef.current
        if (!node) return
        // Na desktopie kontener jest gridem — nie ma czego śledzić.
        if (node.scrollWidth <= node.clientWidth + 1) return
        const left = node.getBoundingClientRect().left
        let best = 0
        let bestDist = Number.POSITIVE_INFINITY
        for (let i = 0; i < node.children.length; i++) {
          const child = node.children[i] as HTMLElement
          const dist = Math.abs(child.getBoundingClientRect().left - left)
          if (dist < bestDist) {
            bestDist = dist
            best = i
          }
        }
        setSelectedIndex((prev) => (prev === best ? prev : best))
      })
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
    // `isLoading` z tego samego powodu co w efekcie wyżej: przy pierwszym
    // wejściu na ekranie jest spinner, karuzeli nie ma w DOM i `scrollerRef`
    // jest puste. Bez ponownego przebiegu listener nie podpiąłby się nigdy,
    // a pasek dni przestałby nadążać za przesuwaniem palcem.
  }, [isLoading])

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-border-subtle bg-bg-elevated p-8 text-center">
        <Loader2
          className="h-5 w-5 animate-spin mx-auto text-text-muted"
          aria-hidden
        />
        <p className="text-sm text-text-muted mt-2">Wczytywanie tygodnia…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-danger/40 bg-danger/5 p-4 text-sm text-text-primary">
        Nie udało się wczytać danych tygodnia. Sprawdź połączenie i odśwież stronę.
      </div>
    )
  }

  const selectedDay = days[selectedIndex]

  return (
    <div>
      <DayStrip
        days={days}
        selectedIndex={selectedIndex}
        onSelect={(i) => {
          setSelectedIndex(i)
          scrollToIndex(i, reducedMotion ? 'auto' : 'smooth')
        }}
      />

      {/* Zmiana tygodnia nie gasi już widoku — poprzedni tydzień zostaje
          na ekranie, a odświeżanie sygnalizuje dyskretny pasek. */}
      <p
        aria-live="polite"
        className={cn(
          'text-[11px] text-text-muted items-center gap-1.5 mb-2',
          isFetching ? 'flex' : 'hidden',
        )}
      >
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        Odświeżam…
      </p>

      <div
        ref={scrollerRef}
        className={cn(
          'flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2',
          // `overscroll-x-contain` — bez tego dojechanie do końca karuzeli
          // przechodzi w gest „wstecz" iOS i wyrzuca z kalendarza.
          'overscroll-x-contain',
          'lg:grid lg:gap-2 xl:gap-2.5 3xl:gap-3',
          'lg:[grid-template-columns:repeat(5,minmax(0,1fr))]',
          'lg:overflow-visible lg:snap-none lg:pb-0',
        )}
      >
        {days.map((day) => (
          <div
            key={day.date}
            className={cn(
              'snap-start shrink-0',
              // Procent liczy się od kontenera, nie od viewportu — dzięki temu
              // karta zna padding <main> i safe-area notcha. `100vw` ich nie znało
              // i w landsapie wystawało poza widoczną kolumnę.
              'w-[92%] sm:w-[48%]',
              'lg:w-auto lg:shrink',
            )}
          >
            <CalendarWeekCell
              day={day}
              totalActiveUsers={totalActiveUsers}
              inactiveOpenByDefault={isDesktop}
              onUserClick={onUserClick}
            />
          </div>
        ))}
      </div>

      <WeekendActivityNotice entries={weekendActivity} />

      {/* Czytnik ekranu dostaje informację, który dzień jest na wierzchu. */}
      <p aria-live="polite" className="sr-only">
        {selectedDay
          ? `Pokazany dzień: ${selectedDay.weekday}, ${parseInt(
              selectedDay.date.slice(8, 10),
              10,
            )} ${monthLabel(selectedDay.date)}`
          : ''}
      </p>
    </div>
  )
}
